import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { AlertTriangle, Check, CheckCircle2, Download, Loader2, Pencil, Search, X } from "lucide-react";
import api from "../../lib/api";
import UserAvatar from "../../components/UserAvatar";
import Sheet from "../../components/Sheet";
import LunchPicker from "../../components/LunchPicker";
import {
  fmtDur,
  fmtTime,
  paidMinutesOf,
  lunchMinutesOf,
  statusOf,
  fromLocalInput,
  toLocalInput,
} from "../../lib/timecard";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];
const PERIODS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 0, label: "All time" },
];
const REJECT_REASONS = ["Wrong hours", "Lunch is wrong", "Wrong project"];

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

function errorText(err, fallback) {
  const d = err && err.response && err.response.data && err.response.data.detail;
  return typeof d === "string" ? d : fallback;
}

function Chip({ tone = "muted", children }) {
  const tones = {
    muted: "border-border bg-muted text-muted-foreground",
    accent: "border-primary/40 bg-primary/10 text-primary",
    ok: "border-green-500/40 bg-green-500/10 text-green-500",
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-500",
    bad: "border-red-500/40 bg-red-500/10 text-red-400",
  };
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function TimecardCard({ s, busy, onApprove, onReject, onEdit }) {
  const status = statusOf(s);
  const outside = s.within_geofence === false || s.out_within_geofence === false;
  const lastEdit = Array.isArray(s.edits) && s.edits.length > 0 ? s.edits[s.edits.length - 1] : null;
  const lunch = lunchMinutesOf(s);

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`timecard-${s.id}`}>
      <div className="flex items-start gap-3">
        <UserAvatar url={s.avatar_url} name={s.user_name} size={44} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{s.user_name}</div>
          <div className="truncate text-sm text-muted-foreground">{s.jobsite_name}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-xl font-bold">{fmtDur(paidMinutesOf(s, Date.now()))}</div>
          <div className="font-mono text-[11px] text-muted-foreground">{lunch > 0 ? `lunch ${lunch} min` : "no lunch"}</div>
        </div>
      </div>

      <div className="mt-3 font-mono text-sm text-muted-foreground">
        {fmtDate(s.clock_in)} | {fmtTime(s.clock_in)} - {fmtTime(s.clock_out)}
      </div>

      {s.note && <div className="mt-2 text-sm text-muted-foreground">“{s.note}”</div>}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {status === "pending" && <Chip tone="warn">Awaiting approval</Chip>}
        {status === "approved" && <Chip tone="ok">Approved</Chip>}
        {status === "rejected" && <Chip tone="bad">Rejected</Chip>}
        {s.edited && <Chip tone="accent">Edited</Chip>}
        {s.location_missing ? (
          <Chip tone="warn">No location</Chip>
        ) : outside ? (
          <Chip tone="warn">Outside geofence</Chip>
        ) : s.within_geofence === true ? (
          <Chip tone="ok">Geo OK</Chip>
        ) : null}
      </div>

      {status === "rejected" && s.reject_reason && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm">
          <span className="font-semibold">{s.rejected_by || "Reviewer"}:</span> {s.reject_reason}
        </div>
      )}
      {status === "approved" && s.approved_by && (
        <div className="mt-2 text-xs text-muted-foreground">Approved by {s.approved_by}</div>
      )}
      {lastEdit && (
        <div className="mt-2 text-xs text-muted-foreground">
          Edited by {lastEdit.by}
          {lastEdit.reason ? `: ${lastEdit.reason}` : ""}
        </div>
      )}

      <div className={`mt-4 grid gap-2 ${status === "pending" ? "grid-cols-3" : status === "rejected" ? "grid-cols-2" : "grid-cols-1"}`}>
        {status === "pending" && (
          <button
            type="button"
            onClick={() => onReject(s)}
            disabled={busy}
            className="flex h-12 items-center justify-center gap-1.5 rounded-lg border border-red-500/50 text-sm font-semibold text-red-400 disabled:opacity-50"
            data-testid={`reject-${s.id}`}
          >
            <X className="h-4 w-4" /> Reject
          </button>
        )}
        <button
          type="button"
          onClick={() => onEdit(s)}
          disabled={busy}
          className="flex h-12 items-center justify-center gap-1.5 rounded-lg border border-border text-sm font-semibold disabled:opacity-50"
          data-testid={`edit-${s.id}`}
        >
          <Pencil className="h-4 w-4" /> Edit
        </button>
        {status !== "approved" && (
          <button
            type="button"
            onClick={() => onApprove(s)}
            disabled={busy}
            className="flex h-12 items-center justify-center gap-1.5 rounded-lg bg-primary text-sm font-semibold text-black disabled:opacity-50"
            data-testid={`approve-${s.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
          </button>
        )}
      </div>
    </div>
  );
}

function EditSheet({ shift, onClose, onSaved }) {
  const originalIn = toLocalInput(shift.clock_in);
  const originalOut = toLocalInput(shift.clock_out);
  const originalLunch = typeof shift.lunch_minutes === "number" ? shift.lunch_minutes : lunchMinutesOf(shift);

  const [inV, setInV] = useState(originalIn);
  const [outV, setOutV] = useState(originalOut);
  const [lunch, setLunch] = useState(originalLunch);
  const [note, setNote] = useState(shift.note || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const timesChanged = inV !== originalIn || outV !== originalOut;
  const lunchChanged = lunch !== originalLunch;
  const noteChanged = note !== (shift.note || "");
  const changed = timesChanged || lunchChanged || noteChanged;

  const inIso = fromLocalInput(inV);
  const outIso = fromLocalInput(outV);
  const invalid = !inIso || !outIso || Date.parse(outIso) <= Date.parse(inIso);
  const preview = invalid ? null : paidMinutesOf({ clock_in: inIso, clock_out: outIso, lunch_minutes: lunch }, 0);

  const save = async (approve) => {
    if (invalid) {
      toast.error("The clock out must be after the clock in.");
      return;
    }
    if (timesChanged && !reason.trim()) {
      toast.error("Add a short reason for changing the times.");
      return;
    }
    const body = { reason: reason.trim(), approve };
    if (inV !== originalIn) body.clock_in = inIso;
    if (outV !== originalOut) body.clock_out = outIso;
    if (lunchChanged) body.lunch_minutes = lunch;
    if (noteChanged) body.note = note;
    setSaving(true);
    try {
      await api.patch(`/attendance/${shift.id}/adjust`, body);
      toast.success(approve ? "Saved and approved" : "Timecard saved");
      onSaved();
    } catch (err) {
      toast.error(errorText(err, "Could not save the timecard."));
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "h-12 w-full rounded-lg border border-input bg-background px-3 text-base outline-none focus:border-primary";
  const alreadyApproved = statusOf(shift) === "approved";
  const history = Array.isArray(shift.edits) ? [...shift.edits].reverse().slice(0, 3) : [];

  return (
    <Sheet
      title="Edit timecard"
      subtitle={`${shift.user_name} | ${shift.jobsite_name}`}
      onClose={() => !saving && onClose()}
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => save(false)}
            disabled={saving || !changed}
            className="h-12 flex-1 rounded-xl border border-border text-base font-semibold disabled:opacity-50"
            data-testid="edit-save"
          >
            Save
          </button>
          {!alreadyApproved && (
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-black disabled:opacity-60"
              data-testid="edit-save-approve"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save and approve
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="edit-in" className="mb-1.5 block text-sm font-medium">
              Clock in
            </label>
            <input id="edit-in" type="datetime-local" value={inV} onChange={(e) => setInV(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label htmlFor="edit-out" className="mb-1.5 block text-sm font-medium">
              Clock out
            </label>
            <input id="edit-out" type="datetime-local" value={outV} onChange={(e) => setOutV(e.target.value)} className={fieldClass} />
          </div>
        </div>

        <div>
          <div className="mb-2 text-sm font-medium">Lunch time</div>
          <LunchPicker value={lunch} onChange={setLunch} disabled={saving} />
        </div>

        <div className="flex items-end justify-between rounded-lg border border-border bg-background p-4">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Paid time</span>
          <span className="font-mono text-2xl font-bold">{preview === null ? "--" : fmtDur(preview)}</span>
        </div>

        <div>
          <label htmlFor="edit-note" className="mb-1.5 block text-sm font-medium">
            Description
          </label>
          <textarea
            id="edit-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
          />
        </div>

        {timesChanged && (
          <div>
            <label htmlFor="edit-reason" className="mb-1.5 block text-sm font-medium">
              Why are you changing the times? <span className="text-red-400">(required)</span>
            </label>
            <input
              id="edit-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={300}
              placeholder="Forgot to clock out"
              className={fieldClass}
            />
          </div>
        )}

        {history.length > 0 && (
          <div className="rounded-lg border border-border bg-background p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-semibold text-foreground">Change history</div>
            {history.map((h, i) => (
              <div key={i} className="mt-1">
                {h.by} | {new Date(h.at).toLocaleString()}
                {h.reason ? ` | ${h.reason}` : ""}
              </div>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}

export default function AdminTimeTracking() {
  const [tab, setTab] = useState("pending");
  const [period, setPeriod] = useState(30);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadPending = useCallback(async () => {
    const { data } = await api.get("/attendance/history", { params: { status: "pending", limit: 500 } });
    setPending(Array.isArray(data) ? data : []);
  }, []);

  const loadTab = useCallback(async () => {
    if (tab === "pending") {
      setItems([]);
      return;
    }
    const params = { status: tab, limit: 500 };
    if (period > 0) params.since = new Date(Date.now() - period * 86400000).toISOString();
    const { data } = await api.get("/attendance/history", { params });
    setItems(Array.isArray(data) ? data : []);
  }, [tab, period]);

  const reload = useCallback(async () => {
    try {
      await Promise.all([loadPending(), loadTab()]);
    } catch (e) {
      toast.error("Could not load the timecards.");
    }
  }, [loadPending, loadTab]);

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [reload]);

  const list = useMemo(() => {
    const base = tab === "pending" ? pending : items;
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (s) => (s.user_name || "").toLowerCase().includes(q) || (s.jobsite_name || "").toLowerCase().includes(q)
    );
  }, [tab, pending, items, query]);

  const totalMins = useMemo(() => list.reduce((acc, s) => acc + paidMinutesOf(s, Date.now()), 0), [list]);

  const approve = async (s) => {
    setBusyId(s.id);
    try {
      await api.patch(`/attendance/${s.id}/approve`);
      toast.success("Approved");
      await reload();
    } catch (err) {
      toast.error(errorText(err, "Could not approve."));
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (s) => {
    setRejectFor(s);
    setRejectReason("");
  };

  const sendReject = async () => {
    if (!rejectFor) return;
    setRejecting(true);
    try {
      await api.patch(`/attendance/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Sent back to the worker");
      setRejectFor(null);
      await reload();
    } catch (err) {
      toast.error(errorText(err, "Could not reject."));
    } finally {
      setRejecting(false);
    }
  };

  const approveAll = async () => {
    setBulkBusy(true);
    let done = 0;
    try {
      for (const s of list) {
        await api.patch(`/attendance/${s.id}/approve`);
        done += 1;
      }
      toast.success(`${done} timecards approved`);
    } catch (err) {
      toast.error(`${errorText(err, "Something went wrong.")} (${done} approved before the error)`);
    } finally {
      setBulkBusy(false);
      setBulkOpen(false);
      await reload();
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/attendance/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "timecards.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not export the file.");
    }
  };

  const canExport = !Capacitor.isNativePlatform();

  return (
    <div className="mx-auto max-w-4xl px-4 pb-10 pt-5 md:px-8 md:pt-10">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Time Tracking</h1>
          <p className="mt-1 text-sm text-muted-foreground">Review timecards. Approve, send back, or fix the hours.</p>
        </div>
        {canExport && (
          <button
            type="button"
            onClick={exportCsv}
            className="hidden h-11 shrink-0 items-center gap-2 rounded-lg border border-border px-4 font-semibold md:inline-flex"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        )}
      </div>

      {/* Abas */}
      <div className="grid grid-cols-3 rounded-lg border border-border bg-muted p-1" role="tablist" aria-label="Status">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md py-3 text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
            {t.id === "pending" && pending.length > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-black">{pending.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab !== "pending" && (
        <div className="mt-3 flex gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => setPeriod(p.days)}
              className={`h-10 rounded-full border px-4 text-sm font-medium ${
                period === p.days ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by person or project"
          className="h-12 w-full rounded-lg border border-input bg-background pl-11 pr-4 text-base outline-none focus:border-primary"
        />
      </div>

      {/* Resumo */}
      {!loading && list.length > 0 && (
        <div className="mt-4 flex items-center justify-between border-b border-border pb-2">
          <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            {list.length} {list.length === 1 ? "timecard" : "timecards"}
          </div>
          <div className="font-mono text-base font-bold">{fmtDur(totalMins)} paid</div>
        </div>
      )}

      {tab === "pending" && !loading && list.length > 1 && (
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary font-semibold text-black"
          data-testid="approve-all"
        >
          <Check className="h-5 w-5" /> Approve all ({list.length})
        </button>
      )}

      {loading && <div className="py-16 text-center text-sm text-muted-foreground">Loading...</div>}

      {!loading && list.length === 0 && (
        <div className="grid place-items-center py-16 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-green-500/10 text-green-500">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <div className="mt-4 text-xl font-semibold">
            {tab === "pending" ? "All caught up" : "Nothing here"}
          </div>
          <div className="mt-1 max-w-xs text-sm text-muted-foreground">
            {tab === "pending"
              ? "No timecards are waiting for approval."
              : "No timecards match this filter."}
          </div>
        </div>
      )}

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {list.map((s) => (
          <TimecardCard
            key={s.id}
            s={s}
            busy={busyId === s.id}
            onApprove={approve}
            onReject={openReject}
            onEdit={setEditFor}
          />
        ))}
      </div>

      {/* Rejeitar */}
      {rejectFor && (
        <Sheet
          title="Send back to the worker"
          subtitle={`${rejectFor.user_name} | ${fmtDate(rejectFor.clock_in)}`}
          onClose={() => !rejecting && setRejectFor(null)}
          footer={
            <button
              type="button"
              onClick={sendReject}
              disabled={rejecting}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-destructive text-base font-semibold text-destructive-foreground disabled:opacity-60"
              data-testid="send-reject"
            >
              {rejecting && <Loader2 className="h-4 w-4 animate-spin" />} Reject timecard
            </button>
          }
        >
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {REJECT_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRejectReason(r)}
                  className={`h-10 rounded-full border px-4 text-sm font-medium ${
                    rejectReason === r ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <div>
              <label htmlFor="reject-reason" className="mb-1.5 block text-sm font-medium">
                What should they fix?
              </label>
              <textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={300}
                className="w-full resize-none rounded-lg border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              The worker sees your note, fixes the lunch or description, and sends it again.
            </div>
          </div>
        </Sheet>
      )}

      {/* Editar */}
      {editFor && (
        <EditSheet
          shift={editFor}
          onClose={() => setEditFor(null)}
          onSaved={async () => {
            setEditFor(null);
            await reload();
          }}
        />
      )}

      {/* Aprovar todos */}
      {bulkOpen && (
        <Sheet
          title={`Approve ${list.length} timecards?`}
          subtitle={`${fmtDur(totalMins)} of paid time in total`}
          onClose={() => !bulkBusy && setBulkOpen(false)}
          footer={
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                disabled={bulkBusy}
                className="h-12 flex-1 rounded-xl border border-border text-base font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={approveAll}
                disabled={bulkBusy}
                className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-black disabled:opacity-60"
                data-testid="confirm-approve-all"
              >
                {bulkBusy && <Loader2 className="h-4 w-4 animate-spin" />} Approve all
              </button>
            </div>
          }
        >
          <p className="text-sm text-muted-foreground">
            Only approve all if you already checked the hours. You can still edit an approved timecard later.
          </p>
        </Sheet>
      )}
    </div>
  );
}
